import type { GradientStyle } from '../types';

// Theme configuration for consistent styling across the application
export const theme = {
  // Background gradients
  gradients: {
    primary: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)', // Shadow Realm - Dark Grey/Charcoal gradient
    secondary: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 50%, #db2777 100%)',
    success: 'linear-gradient(135deg, #059669 0%, #0d9488 50%, #0891b2 100%)',
    warning: 'linear-gradient(135deg, #d97706 0%, #dc2626 50%, #b91c1c 100%)',
  },

  // Color palette
  colors: {
    primary: {
      bg: '#64748b', // Brighter slate grey to match Shadow Realm gradient
      hover: '#475569', // Darker slate grey for hover
      text: '#ffffff',
      muted: '#cbd5e1', // Light slate grey for muted
    },
    secondary: {
      bg: '#6b7280',
      hover: '#4b5563',
      text: '#ffffff',
      muted: '#e5e7eb',
    },
    accent: {
      bg: '#8b5cf6',
      hover: '#7c3aed',
      text: '#ffffff',
      muted: '#ede9fe',
    },
    surface: {
      glass: 'rgba(255, 255, 255, 0.1)',
      glassStrong: 'rgba(255, 255, 255, 0.9)',
      glassWeak: 'rgba(255, 255, 255, 0.05)',
      glassDark: 'rgba(0, 0, 0, 0.3)', // Dark glass with 30% opacity
    },
    text: {
      primary: '#ffffff',
      secondary: '#e0e7ff',
      muted: '#c7d2fe',
      error: '#fca5a5',
    },
  },

  // Spacing
  spacing: {
    xs: '0.5rem',
    sm: '1rem',
    md: '1.5rem',
    lg: '2rem',
    xl: '3rem',
  },

  // Border radius
  borderRadius: {
    sm: '0.375rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
  },

  // Shadows
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },

  // Animation durations
  animations: {
    fast: '150ms',
    normal: '300ms',
    slow: '500ms',
  },
} as const;

// Page layout styles
export const pageStyles = {
  // Full-screen gradient background
  gradientBackground: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'flex-start', // Changed from 'center' to align to top
    justifyContent: 'center',
    background: theme.gradients.primary,
  },

  // Container for centered content
  container: {
    maxWidth: '28rem',
    margin: '0 auto',
    textAlign: 'center' as const,
    padding: '0.5rem 1.5rem 1.5rem 1.5rem', // Reduced top padding from 1.5rem to 0.5rem
  },

  // Header section with logo and title
  header: {
    marginBottom: '1rem', // Reduced from 2rem to 1rem
  },

  // Logo container
  logo: {
    width: '6rem',
    height: '6rem',
    margin: '0 auto 1rem',
    backgroundColor: theme.colors.primary.bg,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Logo icon
  logoIcon: {
    width: '3rem',
    height: '3rem',
    color: theme.colors.primary.text,
  },

  // Page title
  title: {
    fontSize: '1.875rem',
    fontWeight: 'bold',
    color: theme.colors.text.primary,
    marginBottom: '0.5rem',
  },

  // Page subtitle
  subtitle: {
    color: theme.colors.text.secondary,
  },

  // Glass effect form container
  formContainer: {
    backgroundColor: theme.colors.surface.glassDark,
    backdropFilter: 'blur(8px)',
    padding: '1rem 1rem 2rem 1rem', // Reduced top padding from 2rem to 1rem
    borderRadius: theme.borderRadius.lg,
    boxShadow: theme.shadows.xl,
    border: `1px solid rgba(255, 255, 255, 0.15)`,
  },

  // Form styling
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1.5rem',
  },

  // Input styling
  input: {
    backgroundColor: theme.colors.surface.glassStrong,
  },

  // Button styling
  button: {
    primary: {
      backgroundColor: theme.colors.primary.bg,
      color: theme.colors.primary.text,
      '&:hover': {
        backgroundColor: theme.colors.primary.hover,
      },
    },
    secondary: {
      backgroundColor: theme.colors.secondary.bg,
      color: theme.colors.secondary.text,
      '&:hover': {
        backgroundColor: theme.colors.secondary.hover,
      },
    },
    ghost: {
      backgroundColor: 'transparent',
      color: theme.colors.text.secondary,
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        color: theme.colors.text.primary,
      },
    },
  },

  // Error message styling
  error: {
    backgroundColor: 'rgba(254, 226, 226, 0.9)',
    border: '1px solid #fecaca',
    borderRadius: theme.borderRadius.md,
    padding: '1rem',
  },

  // Link styling
  link: {
    primary: {
      color: theme.colors.text.secondary,
      '&:hover': {
        color: theme.colors.text.primary,
      },
    },
    secondary: {
      color: theme.colors.text.muted,
      '&:hover': {
        color: theme.colors.text.secondary,
      },
    },
  },

  // Success screen styling
  success: {
    container: {
      textAlign: 'center' as const,
    },
    icon: {
      width: '3rem',
      height: '3rem',
      margin: '0 auto 1rem',
      backgroundColor: 'rgba(34, 197, 94, 0.1)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    iconSvg: {
      width: '1.5rem',
      height: '1.5rem',
      color: '#22c55e',
    },
  },

} as const;

/**
 * Build a CSS linear gradient string from a GradientStyle object
 * @param style - The gradient style configuration
 * @returns CSS linear-gradient string
 */
export function buildGradient(style: GradientStyle): string {
  const angle = style.angle ?? 135;
  
  // Validate that we have at least 2 colors
  if (!style.colors || style.colors.length < 2) {
    console.warn('Invalid gradient: requires at least 2 colors, falling back to default');
    return theme.gradients.primary;
  }
  
  // Build color stops with evenly distributed positions
  const colorStops = style.colors.map((color, idx) => {
    const position = (idx / (style.colors.length - 1)) * 100;
    return `${color} ${position}%`;
  }).join(', ');
  
  return `linear-gradient(${angle}deg, ${colorStops})`;
}

/**
 * Get the default gradient for the application
 * @returns CSS linear-gradient string for the default theme
 */
export function getDefaultGradient(): string {
  return theme.gradients.primary;
}

/**
 * Get the default button color for the application
 * @returns CSS color string for the default button theme
 */
export function getDefaultButtonColor(): string {
  return '#64748b'; // Shadow Realm brighter slate grey
}

/**
 * Lighten a hex color by a percentage
 * @param color - Hex color string (#RRGGBB or #RGB)
 * @param percent - Percentage to lighten (0-100)
 * @returns Lightened hex color
 */
function lightenColor(color: string, percent: number): string {
  // Handle short hex format (#RGB)
  let hex = color.replace('#', '');
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }
  
  // Parse RGB components
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Lighten each component
  const lighten = (component: number) => {
    const amount = Math.round((255 - component) * (percent / 100));
    return Math.min(255, component + amount);
  };
  
  const newR = lighten(r);
  const newG = lighten(g);
  const newB = lighten(b);
  
  // Convert back to hex
  const toHex = (n: number) => {
    const hex = n.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
}

/**
 * Build a button background style from a button color
 * Creates a subtle gradient effect for visual depth
 * @param color - The base color for the button (CSS color string)
 * @returns CSS background style object
 */
export function buildButtonBackground(color: string): string {
  // For hex colors, create a subtle gradient
  if (color.startsWith('#')) {
    const lighterColor = lightenColor(color, 10);
    return `linear-gradient(135deg, ${color}, ${lighterColor})`;
  }
  
  // For other color formats (rgb, hsl, named), use solid color with subtle opacity variation
  return color;
}

/**
 * Check if a color value is valid for CSS use
 * @param color - Color string to validate
 * @returns true if the color appears to be valid
 */
export function isValidColor(color: string | undefined): boolean {
  if (!color || typeof color !== 'string') {
    return false;
  }
  
  // Basic validation - check for common color formats
  const hexPattern = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;
  const rgbPattern = /^rgba?\(.*\)$/;
  const hslPattern = /^hsla?\(.*\)$/;
  
  return hexPattern.test(color) || 
         rgbPattern.test(color) || 
         hslPattern.test(color) ||
         color.match(/^[a-z]+$/i) !== null; // Named colors
}

export default theme;
